export interface ReferralData {
  referralCode?: string;
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_term?: string;
  utm_content?: string;
}

export interface ReferralMetadata {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_term?: string;
  utm_content?: string;
  captured_at?: string;
  [key: string]: string | undefined;
}

const REFERRAL_STORAGE_KEY = 'zubeai_referral_data';

/**
 * Parse referral data from URL search parameters
 */
export function parseReferralFromUrl(searchParams: URLSearchParams): ReferralData | null {
  return {
    referralCode: searchParams.get('referral') || undefined,
    utm_source: searchParams.get('utm_source') || undefined,
    utm_medium: searchParams.get('utm_medium') || undefined,
    utm_campaign: searchParams.get('utm_campaign') || undefined,
    utm_term: searchParams.get('utm_term') || undefined,
    utm_content: searchParams.get('utm_content') || undefined,
  };
}

/**
 * Store referral data in localStorage
 */
export function storeReferralData(data: ReferralData): void {
  try {
    localStorage.setItem(REFERRAL_STORAGE_KEY, JSON.stringify(data));
  } catch (error) {
    console.error('Failed to store referral data:', error);
  }
}

/**
 * Retrieve stored referral data from localStorage
 */
export function getStoredReferralData(): ReferralData | null {
  try {
    const stored = localStorage.getItem(REFERRAL_STORAGE_KEY);
    return stored ? JSON.parse(stored) : null;
  } catch (error) {
    console.error('Failed to retrieve referral data:', error);
    return null;
  }
}

/**
 * Clear stored referral data
 */
export function clearReferralData(): void {
  try {
    localStorage.removeItem(REFERRAL_STORAGE_KEY);
  } catch (error) {
    console.error('Failed to clear referral data:', error);
  }
}

/**
 * Check if the current URL contains referral parameters and store them
 */
export function captureReferralFromCurrentUrl(): ReferralData | null {
  if (typeof window === 'undefined') return null;

  const searchParams = new URLSearchParams(window.location.search);
  const referralData = parseReferralFromUrl(searchParams);

  if (referralData) {
    storeReferralData(referralData);
  }

  return referralData;
}

/**
 * Get referral code for use in forms (prioritizes stored data)
 */
export function getReferralCodeForForm(): string | undefined {
  const storedData = getStoredReferralData();
  return storedData?.referralCode;
}

/**
 * Append referral parameters to a URL if they exist
 */
export function appendReferralParamsToUrl(url: string): string {
  const storedData = getStoredReferralData();
  if (!storedData) return url;

  const urlObj = new URL(url, window.location.origin);

  if (storedData.referralCode) {
    urlObj.searchParams.set('referral', storedData.referralCode);
  }
  if (storedData.utm_source) {
    urlObj.searchParams.set('utm_source', storedData.utm_source);
  }
  if (storedData.utm_medium) {
    urlObj.searchParams.set('utm_medium', storedData.utm_medium);
  }
  if (storedData.utm_campaign) {
    urlObj.searchParams.set('utm_campaign', storedData.utm_campaign);
  }
  if (storedData.utm_term) {
    urlObj.searchParams.set('utm_term', storedData.utm_term);
  }
  if (storedData.utm_content) {
    urlObj.searchParams.set('utm_content', storedData.utm_content);
  }

  return urlObj.toString();
}

/**
 * Build metadata object from stored referral data
 */
export function buildReferralMetadata(): Record<string, string> {
  const storedData = getStoredReferralData();
  if (!storedData) return {};

  const metadata: Record<string, string> = {};

  if (storedData.utm_source) metadata.utm_source = storedData.utm_source;
  if (storedData.utm_medium) metadata.utm_medium = storedData.utm_medium;
  if (storedData.utm_campaign) metadata.utm_campaign = storedData.utm_campaign;
  if (storedData.utm_term) metadata.utm_term = storedData.utm_term;
  if (storedData.utm_content) metadata.utm_content = storedData.utm_content;

  // Add timestamp for when referral was captured
  metadata.captured_at = new Date().toISOString();

  return metadata;
}

/**
 * Check if there's any referral data (code or UTM) to process
 */
export function hasReferralData(): boolean {
  const storedData = getStoredReferralData();
  if (!storedData) return false;

  return !!(
    storedData.referralCode ||
    storedData.utm_source ||
    storedData.utm_medium ||
    storedData.utm_campaign ||
    storedData.utm_term ||
    storedData.utm_content
  );
} 